'use client'
import { useEffect, useRef, useCallback, useMemo } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import styles from './Reviews.module.css';

// Register ScrollTrigger plugin
gsap.registerPlugin(ScrollTrigger);

// Signal-like state management using refs (no re-renders)
const createVideoSignal = () => {
    const state = { isPlaying: false };
    const listeners = new Set();

    return {
        get: () => state.isPlaying,
        set: (value) => {
            state.isPlaying = value;
            listeners.forEach(listener => listener(value));
        },
        subscribe: (listener) => {
            listeners.add(listener);
            return () => listeners.delete(listener);
        }
    };
};

export default function Reviews() {
    const sectionRef = useRef(null);
    const cardsRef = useRef([]);
    const headerRef1 = useRef(null);
    const headerRef2 = useRef(null);
    const video1Ref = useRef(null);
    const video2Ref = useRef(null);

    // Signal-like state for videos (no re-renders)
    const video1Signal = useRef(createVideoSignal());
    const video2Signal = useRef(createVideoSignal());

    // Button refs for direct DOM updates
    const button1Ref = useRef(null);
    const button2Ref = useRef(null);

    // Optimized video controls with direct DOM manipulation
    const toggleVideo1 = useCallback(() => {
        const video = video1Ref.current;
        const button = button1Ref.current;
        if (!video || !button) return;

        const isPlaying = video1Signal.current.get();

        if (isPlaying) {
            video.pause();
            video.muted = true;
            button.textContent = "▶️";
        } else {
            video.play().catch(console.error);
            video.muted = false;
            button.textContent = "⏸️";
        }

        video1Signal.current.set(!isPlaying);
    }, []);

    const toggleVideo2 = useCallback(() => {
        const video = video2Ref.current;
        const button = button2Ref.current;
        if (!video || !button) return;

        const isPlaying = video2Signal.current.get();

        if (isPlaying) {
            video.pause();
            video.muted = true;
            button.textContent = "▶️";
        } else {
            video.play().catch(console.error);
            video.muted = false;
            button.textContent = "⏸️";
        }

        video2Signal.current.set(!isPlaying);
    }, []);

    // Memoized card positions to avoid recalculation
    const cardPositions = useMemo(() => [
        { endX: -150, endY: 0 },     // First card - left side
        { endX: 0, endY: -50 },      // Featured card - center
        { endX: 150, endY: 0 },      // Third card - right side
        { endX: -100, endY: 100 },   // Fourth card - back left
        { endX: 100, endY: 100 }     // Fifth card - back right
    ], []);

    // Optimized intersection observer for lazy video loading
    useEffect(() => {
        const videos = [video1Ref.current, video2Ref.current].filter(Boolean);

        const observer = new IntersectionObserver((entries) => {
            entries.forEach(entry => {
                const video = entry.target;
                if (entry.isIntersecting) {
                    // Only load video source when visible
                    if (!video.src && video.dataset.src) {
                        video.src = video.dataset.src;
                        video.load();
                    }
                }
            });
        }, { threshold: 0.1 });

        videos.forEach(video => observer.observe(video));

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        const mm = gsap.matchMedia();

        // Desktop: Full animation
        mm.add("(min-width: 860px)", () => {
            const ctx = gsap.context(() => {
                const section = sectionRef.current;
                const cards = cardsRef.current;
                const header1 = headerRef1.current;
                const header2 = headerRef2.current;

                if (!section || cards.length === 0) return;

        // Set initial positions - all cards start from far right
        cards.forEach((card, index) => {
            gsap.set(card, {
                x: window.innerWidth + 200, // Start off-screen right
                y: 100 + (index * 50), // Staggered vertical positions
                rotation: 15 + (index * 5), // Initial rotation
                opacity: 0
            });
        });

        // Set initial 3D flat state for headers
        gsap.set([header1, header2], {
            rotationX: 90, // Start completely flat (90 degrees)
            transformOrigin: "center bottom",
            opacity: 0.3
        });

        const timeline = gsap.timeline({
            scrollTrigger: {
                trigger: section,
                pin: true,
                start: "top top",
                end: "+=240%",
                scrub: 0.65,
                anticipatePin: 1,
                invalidateOnRefresh: true
            }
        });

        timeline.to([header1, header2], {
            rotationX: 0,
            opacity: 1,
            scale: 1,
            duration: 0.18,
            ease: "power2.out"
        });

        cards.forEach((card, index) => {
            const { endX, endY } = cardPositions[index] || { endX: 0, endY: 0 };
            timeline.to(card, {
                x: endX,
                y: endY,
                rotation: -5 + index * 3,
                opacity: 1,
                scale: index === 1 ? 1.1 : 1,
                duration: 0.28,
                ease: "power2.out"
            }, 0.12 + index * 0.15);
        });
            });
            return () => ctx.revert();
        });

        // Mobile: Set final state immediately (no animation)
        mm.add("(max-width: 850px)", () => {
            const cards = cardsRef.current;
            const header1 = headerRef1.current;
            const header2 = headerRef2.current;

            if (cards.length === 0) return;

            // Set headers to final state (fully visible, no 3D effect)
            gsap.set([header1, header2], {
                rotationX: 0,
                opacity: 1,
                scale: 1,
            });

            // Set all cards to visible and stacked vertically for reading
            cards.forEach((card, index) => {
                gsap.set(card, {
                    x: 0,
                    y: 0,
                    rotation: 0,
                    opacity: 1,
                    scale: 1,
                    position: 'relative',
                    marginBottom: '2rem'
                });
            });

            return () => {};
        });

        // Cleanup
        return () => {
            mm.revert();
        };
    }, []);

    return (
        <section ref={sectionRef} className={styles.section}>
            <div className={styles.radial}></div>

            {/* Header */}
            <div className={styles.headerContainer}>
                <h1 ref={headerRef1} className={styles.headerText}>
                    WELLNESS STORIES
                </h1>
                <h1 ref={headerRef2} className={styles.headerText}>
                    SHARED:
                </h1>
            </div>

            {/* Review Cards Container */}
            <div className={styles.reviewsContainer}>

                {/* Review Card 1 */}
                <div ref={el => cardsRef.current[0] = el} className={`${styles.reviewCard} ${styles.videoCard}`}>
                    <div className={styles.videoContainer}>
                        <video
                            ref={video1Ref}
                            data-src={'https://longevity20155.sirv.com/1.mp4'}
                            muted
                            loop
                            preload="none"
                            playsInline
                            className={styles.video}
                        />
                        <button ref={button1Ref} onClick={toggleVideo1} className={styles.playButton}>
                            ▶️
                        </button>
                    </div>
                    <div className={styles.userInfo}>
                        <div className={`${styles.profile} ${styles.profileSmall}`}>MC</div>
                        <div>
                            <p className={`${styles.userName} ${styles.userNameSmall}`}>MARWA CHARRADI.</p>
                        </div>
                    </div>
                </div>

                {/* Review Card 2 - Center/Featured */}
                <div ref={el => cardsRef.current[1] = el} className={`${styles.reviewCard} ${styles.featuredCard}`}>
                    <div>
                        <p className={styles.reviewText}>
                            "Tastes like a candy I used to enjoy as a kid, but it's a vitamin. It tastes amazing!"
                        </p>
                    </div>
                    <div className={styles.userInfo}>
                        <div className={`${styles.profile} ${styles.profileMedium}`}>MR</div>
                        <div>
                            <p className={`${styles.userName} ${styles.userNameMedium}`}>MOURAD ROUGE.</p>
                        </div>
                    </div>
                </div>

                {/* Review Card 3 */}
                <div ref={el => cardsRef.current[2] = el} className={`${styles.reviewCard} ${styles.videoCard}`} style={{background: "#f0f0f0"}}>
                    <div className={styles.videoContainer}>
                        <video
                            ref={video2Ref}
                            data-src={'https://longevity20155.sirv.com/2.mp4'}
                            muted
                            loop
                            preload="none"
                            playsInline
                            className={styles.video}
                        />
                        <button ref={button2Ref} onClick={toggleVideo2} className={styles.playButton}>
                            ▶️
                        </button>
                    </div>
                    <div className={styles.userInfo}>
                        <div className={`${styles.profile} ${styles.profileSmall}`}>CZ</div>
                        <div>
                            <p className={`${styles.userName} ${styles.userNameSmall}`}>CHEYMA ZID.</p>
                        </div>
                    </div>
                </div>

                {/* Review Card 4 - Background */}
                <div ref={el => cardsRef.current[3] = el} className={`${styles.reviewCard} ${styles.backgroundCard}`}>
                    <div>
                        <p className={styles.backgroundText}>
                            "Perfect for my busy lifestyle. Easy to take and actually works!"
                        </p>
                    </div>
                    <div className={styles.userInfo} style={{gap: "0.8vw"}}>
                        <div className={`${styles.profile} ${styles.profileTiny}`}>AM</div>
                        <div>
                            <p className={`${styles.userName} ${styles.userNameTiny}`}>ABDALLAH M.</p>
                        </div>
                    </div>
                </div>

                {/* Review Card 5 - Background */}
                <div ref={el => cardsRef.current[4] = el} className={`${styles.reviewCard} ${styles.backgroundCard2}`}>
                    <div>
                        <p className={styles.backgroundText}>
                            "Great quality and fast shipping. Will definitely order again!"
                        </p>
                    </div>
                    <div className={styles.userInfo} style={{gap: "0.8vw"}}>
                        <div className={`${styles.profile} ${styles.profileTiny}`}>SL</div>
                        <div>
                            <p className={`${styles.userName} ${styles.userNameTiny}`}>Sarah L.</p>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    )

}
